<template>
  <div class="org-reservation-calendar">
    <PageTitleExtra
      :title="displayText.pageTitle"
      :hide-btn="!selectedShopId"
      :btn3="displayText.btn.settings"
      @btn3Click="dialog.settings = true"
    />
    <!-- 分店選擇 -->
    <div class="shop-select-bar">
      <BaseSearch
        :value.sync="selectedShopId"
        :api="apiFormatAdaptor(GetShops)"
        placeholder="輸入店家名稱"
        objKey="id"
        @clear="onShopChange"
        @change="onShopChange"
      />
      <span v-if="!selectedShopId" class="text-gray-80 text-sm ml-3">
        請先選擇分店以檢視行事曆
      </span>
    </div>

    <template v-if="selectedShopId">
      <DisplaySettingsDialog
        v-if="showControl.dialog.settings"
        :length="unitList.length"
        storage-key="orgReservationCalendarSettings"
        :extra-content-options="extraContentOptions"
        @close="dialog.settings = false"
        @confirm="onTimeRangeChange"
      />

      <div class="flex justify-between" style="margin-bottom: 12px">
        <FiltersContainer>
          <el-date-picker
            v-model="search.date"
            editable
            style="width: 340px !important"
            type="date"
            :placeholder="displayText.search.date"
          />
          <BaseElSelect
            v-model="search.unit"
            style="width: 340px !important"
            filterable
            clearable
            :placeholder="displayText.search.unit"
            value-key="id"
            @change="searchUnitChange"
            @clear="delayGenerateCellBlock"
          >
            <BaseElSelectOption
              v-for="unit in unitList"
              :key="unit.id"
              :label="unit.name"
              :value="unit"
            />
          </BaseElSelect>
        </FiltersContainer>
      </div>

      <FlexLayout :gap="20">
        <div class="flex-1">
          <vue-cal
            v-if="showCal"
            v-loading="loading"
            :time-from="startPeriodTime || 0"
            :time-to="lastPeriodTime || 1440"
            :timeCellHeight="columnHeight"
            style="width: 100%; height: 100%"
            active-view="day"
            hide-view-selector
            :selected-date="search.date"
            :events="composEvents"
            :disable-views="['years', 'year', 'month', 'week']"
            :split-days="chunkUnitColumn[page]"
            :sticky-split-labels="true"
            :on-event-click="onEventClick"
            :editable-events="false"
            :time-step="timeUnit"
            @view-change="dateChange"
          >
            <template #title="{ title, view }">
              <div class="relative">
                <p>{{ formateTitle(title, view) }}</p>
                <span
                  class="material-icons absolute cursor-pointer"
                  style="top: 5px; right: -280px"
                  @click="search.date = new Date()"
                >
                  event_outlined
                </span>
              </div>
            </template>

            <template #time-cell="{ hours, minutes }">
              <div :class="{ 'vuecal__time-cell-line': true, hours: !minutes }">
                <strong v-if="!minutes">{{ hours }}:{{ plusZero(minutes) }}</strong>
                <span v-else class="text-sub">{{ hours }}:{{ plusZero(minutes) }}</span>
              </div>
            </template>

            <template #split-label="{ split }">
              <div class="flex flex-col text-center truncate w-full">
                <p class="truncate w-full break-words" :style="`color: ${split.color}`">{{ split.label }}</p>
                <p class="w-full">({{ split.reservationCount }})</p>
              </div>
            </template>

            <template #event="{ event }">
              <div v-if="event.isMore">...</div>
              <div v-else>
                <div class="vuecal__event-time hidden" />
                <div v-if="event.data" :style="`border-radius: 2px; padding: 4px; font-size: ${settings.textSize}px;`">
                  <div class="flex justify-between items-center">
                    <span v-if="event.data.hasComment" class="mr-[4px]">★</span>
                    <p v-if="showControl.reservationTime">
                      {{ formatDate(event.start, 'HH:mm') }} - {{ formatDate(event.end, 'HH:mm') }}
                    </p>
                    <div v-if="event.data.fromNotSpecify" class="tag flex">
                      <span>{{ $t('dailyView.notSpecify') }}</span>
                    </div>
                  </div>
                  <p v-if="showControl.name">{{ event.data.newUserName || displayText.unfilled }}</p>
                  <p v-if="showControl.birthday">{{ event.data.numberCode === 1 ? (get(event.data, 'Member.UserInfo.birthday') ? formatDate(get(event.data, 'Member.UserInfo.birthday'), 'YYYY/MM/DD') : '') : '' }}</p>
                  <p v-if="showControl.phone">{{ event.data.userPhone || displayText.unfilled }}</p>
                  <p v-if="showControl.serviceName">{{ get(event.reservation, 'AppointmentService.name') }}</p>
                  <p v-if="showControl.shopNote && event.data.adminComment" class="truncate">
                    {{ $t('dailyView.display.shopNote') }}：{{ event.data.adminComment }}
                  </p>
                  <p v-for="service in showAttachServices(event.reservation)" :key="service">{{ service }}</p>
                </div>
              </div>
            </template>
          </vue-cal>
        </div>
      </FlexLayout>

      <!-- 預約資訊側邊彈窗（唯讀） -->
      <ReservationOrderDetail
        v-if="showControl.dialog.info"
        :orderId="select.event.id"
        :selecReservation="select.event"
        readonly
        @close="dialog.info = false"
      />
    </template>
  </div>
</template>

<script>
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import { defineComponent, ref, reactive, computed, watch, onMounted, set } from 'vue'
import { useStorage } from '@vueuse/core'
import { get, map, find, flattenDeep, chunk, includes, filter, some } from 'lodash'
import debounce from 'lodash.debounce'

import PageTitleExtra from '@/components/Title/PageTitleExtra.vue'
import DisplaySettingsDialog from '@/views/DailyView/components/DisplaySettingsDialog.vue'
import ReservationOrderDetail from '@/components/Reservation/ReservationOrderDetail/ReservationOrderDetail.vue'

import { GetServiceUnit } from '@/api/serviceUnit'
import { GetReservation, GetReservationCount } from '@/api/reservation'
import { GetPeriods } from '@/api/togglePeriod'
import { GetShops } from '@/api/shop'

import { formatDate, weekDayTranslate } from '@/utils/date'
import { generateTimes, getAllDataFromApi, plusZero, timeToNumber } from '@/utils/helper'
import dayjs from '@/lib/dayjs'
import { apiFormatAdaptor } from '@/utils/api'
import BaseSearch from '@/components/Search/BaseSearch.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'OrgReservationCalendar',
  components: {
    VueCal,
    PageTitleExtra,
    DisplaySettingsDialog,
    ReservationOrderDetail,
    BaseSearch,
  },
  setup () {
    const loading = ref(false)
    const unitList = ref([])

    const settings = useStorage('orgReservationCalendarSettings',
      {
        start: '00:00',
        end: '24:00',
        unitPerPage: 5,
        columnHeight: 180,
        textSize: 12,
        displayItem: ['phone', 'name', 'birthday', 'reservationTime', 'serviceName', 'shopNote'],
      },
      localStorage,
      { mergeDefaults: true },
    )

    const displayItem = ref(get(settings.value, 'displayItem'))
    const columnHeight = ref(get(settings.value, 'columnHeight'))
    const page = ref(0)
    const perPage = ref(get(settings.value, 'unitPerPage'))
    const curStart = ref(null)
    const timeUnit = ref(60)
    const timeOffset = ref(0)
    const showCal = ref(true)
    const startPeriodTime = ref(timeToNumber(get(settings.value, 'start')))
    const lastPeriodTime = ref(timeToNumber(get(settings.value, 'end')))

    const search = reactive({
      unit: null,
      date: new Date(),
    })
    const select = reactive({
      start: null,
      unit: null,
      event: null,
      period: null,
    })
    const dialog = reactive({
      info: false,
      settings: false,
    })
    const selectedShopId = ref('')

    const extraContentOptions = computed(() => [
      { label: t('dailyView.display.shopNote'), value: 'shopNote' },
    ])

    const unitColumn = computed(() => {
      return map(unitList.value, (unit) => ({
        id: unit.id,
        label: unit.name,
        reservationCount: unit.reservationCount,
      }))
    })
    const chunkUnitColumn = computed(() => {
      const unitChunk = chunk(unitColumn.value, perPage.value)
      if (search.unit) {
        unitChunk[page.value] = unitChunk[page.value].filter(i => i.id.includes(search.unit.id))
      }
      return unitChunk
    })
    const composEvents = computed(() => {
      return flattenDeep(map(unitList.value, 'events').filter(i => Boolean(i)))
    })
    const periods = computed(() => {
      return generateTimes(timeUnit.value, timeOffset.value)
    })
    const displayText = computed(() => {
      return {
        pageTitle: t('dailyView.orgPageTitle'),
        btn: {
          settings: t('dailyView.btn.settings'),
        },
        search: {
          date: t('dailyView.search.date'),
          unit: t('dailyView.search.unit'),
        },
        unfilled: t('dailyView.unfilled'),
      }
    })
    const showControl = computed(() => {
      const item = displayItem.value
      return {
        showCal: showCal.value,
        reservationTime: includes(item, 'reservationTime'),
        name: includes(item, 'name'),
        phone: includes(item, 'phone'),
        birthday: includes(item, 'birthday'),
        serviceName: includes(item, 'serviceName'),
        shopNote: includes(item, 'shopNote'),
        dialog: {
          info: dialog.info,
          settings: dialog.settings,
        },
      }
    })

    watch(unitColumn, () => {
      setTimeout(() => {
        generateCellBlock()
      }, 300)
    })
    watch(page, () => {
      setTimeout(() => {
        generateCellBlock()
      }, 300)
    })
    watch(startPeriodTime, () => {
      setTimeout(() => {
        generateCellBlock()
      }, 300)
    })
    watch(lastPeriodTime, () => {
      setTimeout(() => {
        generateCellBlock()
      }, 300)
    })

    const getDailyViewSettings = () => {
      startPeriodTime.value = timeToNumber(get(settings.value, 'start'))
      lastPeriodTime.value = timeToNumber(get(settings.value, 'end'))
      perPage.value = get(settings.value, 'unitPerPage')
      displayItem.value = get(settings.value, 'displayItem')
      columnHeight.value = get(settings.value, 'columnHeight')
    }
    const onTimeRangeChange = () => {
      showCal.value = false
      getDailyViewSettings()
      dateChange({ startDate: curStart.value })
      generateCellBlock()
      showCal.value = true
      dialog.settings = false
    }
    const searchUnitChange = (e) => {
      const unitColumn = map(unitList.value, 'id')
      const chunkColumn = chunk(unitColumn, perPage.value)
      for (const curePage in chunkColumn) {
        if (chunkColumn[curePage].includes(e.id)) {
          page.value = Number(curePage)
          if (page.value === Number(curePage)) {
            setTimeout(() => {
              generateCellBlock()
            }, 300)
          }
          return
        }
      }
    }
    const changePage = (curPage) => {
      page.value = curPage - 1
      search.unit = null
    }
    const formateTitle = (title, view) => {
      const date = formatDate(view.startDate, 'YYYY/MM/DD')
      const day = weekDayTranslate(dayjs(view.startDate).day())
      return `${date} ${day}`
    }
    const onEventClick = (event, e) => {
      if (event.isMore) {
        const unit = find(unitList.value, { id: event.split })
        search.unit = unit
        return
      }
      if (event.disabled) return
      if (event.noShift) return
      select.event = event.reservation
      dialog.info = true
    }

    const generateCellBlock = () => {
      const cols = document.querySelectorAll('[data-split]')
      cols.forEach(el => {
        const unitId = el.getAttribute('data-split')
        const unit = find(unitList.value, { id: unitId })
        const target = document.querySelector(`[data-split="${unit.id}"]`)
        const oldCellBlocks = Array.from(target.children).filter(i => Array.from(i.classList).includes('cell-block'))
        const noEventBlock = Array.from(target.children).filter(i => Array.from(i.classList).includes('vuecal__no-event'))
        if (noEventBlock.length) target.removeChild(noEventBlock[0])
        oldCellBlocks.forEach(i => target.removeChild(i))
        periods.value.forEach(period => {
          if (timeToNumber(period) >= startPeriodTime.value && timeToNumber(period) < lastPeriodTime.value) {
            const block = document.createElement('div')
            block.style.height = `${columnHeight.value}px`
            block.classList.add('cell-block')
            block.addEventListener('click', () => {
              // No-op for read-only view
            })
            target.appendChild(block)
          }
        })
      })
    }

    const getServiceUnit = async () => {
      try {
        unitList.value = await GetServiceUnit({
          shopId: selectedShopId.value,
        })
      } catch (error) {
        console.log(error)
      }
    }
    const getUnitDayPeriods = async (unit, date) => {
      const res = await GetPeriods({
        shopId: selectedShopId.value,
        appointmentUnitId: unit.id,
        start: dayjs(date).startOf('date').toDate(),
        end: dayjs(date).endOf('date').toDate(),
      })
      const targetUnit = find(unitList.value, { id: unit.id })
      if (targetUnit) set(targetUnit, 'times', res.times)
      if (!res.times.length) {
        set(targetUnit, 'events', [
          {
            start: dayjs(date).startOf('date').toDate(),
            end: dayjs(date).endOf('date').toDate(),
            allDay: true,
            class: 'no-shift',
            split: targetUnit.id,
            disabled: true,
            noShift: true,
            background: true,
          },
        ])
      }
      if (res.times.length) {
        createPeriodEvents(targetUnit, res.times)
      }
    }
    const isSamePeriod = (periodA, periodB) => {
      const a = dayjs(periodA).format('HH:mm')
      const b = dayjs(periodB).format('HH:mm')
      return a === b
    }
    const periodToDate = (period) => {
      const now = dayjs(curStart.value).format('YYYY/MM/DD')
      return dayjs(`${now} ${period}`).toDate()
    }

    const createPeriodEvents = async (unit, times) => {
      if (!times.length) return
      const startPeriod = periodToDate(periods.value[0])
      const firstPeriod = times[0].start
      const endPeriod = periodToDate(periods.value[periods.value.length - 1])
      const lastPeriod = times[times.length - 1].start

      if (!isSamePeriod(startPeriod, firstPeriod)) {
        unit.events.push({
          start: startPeriod,
          end: new Date(firstPeriod),
          class: 'no-shift',
          split: unit.id,
          disabled: true,
          noShift: true,
          background: true,
        })
      }
      let cur = dayjs(times[0].start)
      let start
      let last
      while (!cur.isAfter(dayjs(times[times.length - 1].start).add(timeUnit.value, 'm'))) {
        const exist = find(times, t => dayjs(t.start).isSame(cur))
        if (exist) {
          if (exist.status === 'available') {
            if (last === 'noShift') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'no-shift',
                  background: true,
                  split: unit.id,
                  noShift: true,
                })
                start = null
              }
            }
            if (last === 'available') {
              start = null
            }
            if (last === 'ban') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'disabled',
                  background: true,
                  split: unit.id,
                  disabled: true,
                })
                start = null
              }
            }
            if (last === 'booking') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'disabled',
                  background: true,
                  split: unit.id,
                  disabled: true,
                })
                start = null
              }
            }
            last = 'available'
          }
          if (exist.status === 'ban') {
            if (last === 'noShift') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'no-shift',
                  background: true,
                  split: unit.id,
                  noShift: true,
                })
                start = null
              }
            }
            if (!start) start = dayjs(exist.start)
            last = 'ban'
          }
          if (exist.status === 'booking') {
            if (!start) start = dayjs(exist.start)
            if (last === 'noShift') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'no-shift',
                  background: true,
                  split: unit.id,
                  noShift: true,
                })
              }
            }
            if (last === 'ban') {
              if (start) {
                unit.events.push({
                  start: start.toDate(),
                  end: cur.toDate(),
                  class: 'disabled',
                  background: true,
                  split: unit.id,
                  disabled: true,
                })
              }
            }
            last = 'booking'
          }
        }
        if (!exist) {
          if (last === 'ban') {
            if (start) {
              unit.events.push({
                start: start.toDate(),
                end: cur.toDate(),
                class: 'disabled',
                background: true,
                split: unit.id,
                disabled: true,
              })
              start = null
            }
          }
          if (!start) start = cur
          last = 'noShift'
        }
        cur = cur.add(timeUnit.value, 'm')
      }
      if (!isSamePeriod(lastPeriod, endPeriod)) {
        unit.events.push({
          start: dayjs(lastPeriod).add(timeUnit.value, 'm').toDate(),
          end: endPeriod,
          class: 'no-shift',
          split: unit.id,
          disabled: true,
          noShift: true,
          background: true,
        })
      }
    }

    const dateChange = debounce(async (e) => {
      search.date = e.startDate
      loading.value = true
      curStart.value = new Date(e.startDate)
      select.start = new Date(e.startDate)
      for (const unit of unitList.value) {
        set(unit, 'events', [])
        set(unit, 'times', [])
        set(unit, 'reservationCount', 0)
        getUnitDayPeriods(unit, new Date(e.startDate))
      }
      await getReservation(new Date(e.startDate))
      loading.value = false
    }, 300, { leading: false, trailing: true })

    const getReservation = async (date) => {
      const payload = {
        shopId: selectedShopId.value,
        dateStart: dayjs(date).startOf('date').toDate(),
        dateEnd: dayjs(date).endOf('date').toDate(),
      }
      const count = await GetReservationCount(payload)
      const getReservationFn = async (payload) => {
        try {
          return await GetReservation(payload)
        } catch (err) {
          return []
        }
      }
      const res = await getAllDataFromApi(count, getReservationFn, { ...payload, limit: 300, start: 0 })
      const reservations = res.filter(i => get(i, 'AppointmentOrder.status') !== 'cancel')
      reservations.forEach(i => createReservationEvents(i))
    }

    const createReservationEvents = (reservation) => {
      if (!reservation) return
      const {
        AppointmentOrder,
        AppointmentUnit,
        numberCode,
        Member,
        start,
        end,
        userName,
        userPhone,
        fromNotSpecify,
      } = reservation
      const newUserName = get(AppointmentOrder, 'origin') === 'pubApt' ? `(訪客)${userName}` : userName
      const unitId = get(AppointmentUnit, 'id')
      if (!unitId) return
      const data = {
        title: '',
        data: {
          newUserName,
          userPhone,
          fromNotSpecify,
          Member,
          numberCode,
          adminComment: get(reservation, 'AppointmentOrder.adminComment') || '',
          hasComment: Boolean(get(reservation, 'AppointmentOrder.adminComment') || get(reservation, 'AppointmentOrder.userComment')),
        },
        start: new Date(start),
        end: new Date(end),
        split: unitId || 'noUnit',
        class: AppointmentOrder.status,
        reservation,
      }
      const target = find(unitList.value, { id: unitId })
      if (target) {
        if (!target.events) {
          target.events = []
        }
        const isDuplicate = some(target.events, (event) => event.start === start && event.end === end)
        if (!isDuplicate) {
          target.events.push(data)
          target.reservationCount = filter(target.events, event => event.reservation).length
        }
      }
    }

    const showAttachServices = (reservation) => {
      const data = get(reservation, 'AppointmentServiceAttaches')
      return map(data, i => i.name)
    }

    const delayGenerateCellBlock = () => {
      setTimeout(() => {
        generateCellBlock()
      }, 300)
    }

    const onShopChange = () => {
      if (!selectedShopId.value) {
        unitList.value = []
        return
      }
      getDailyViewSettings()
      getServiceUnit()
      setTimeout(() => {
        dateChange({ startDate: new Date() })
        generateCellBlock()
      }, 500)
    }

    onMounted(async () => {
      getDailyViewSettings()
    })

    return {
      selectedShopId,
      settings,
      loading,
      unitList,
      page,
      perPage,
      curStart,
      timeUnit,
      showCal,
      startPeriodTime,
      lastPeriodTime,
      search,
      select,
      dialog,
      displayText,
      showControl,
      unitColumn,
      chunkUnitColumn,
      composEvents,
      periods,
      extraContentOptions,
      displayItem,
      columnHeight,
      plusZero,
      get,
      formatDate,
      formateTitle,
      onEventClick,
      onTimeRangeChange,
      searchUnitChange,
      changePage,
      dateChange,
      delayGenerateCellBlock,
      showAttachServices,
      generateCellBlock,
      apiFormatAdaptor,
      GetShops,
      onShopChange,
    }
  },
})
</script>

<style lang="scss" scoped>
.org-reservation-calendar {
  padding-bottom: 0 !important;
}
.shop-select-bar {
  @apply flex items-center mb-[20px];
}

:deep(.vuecal) {
  @apply rounded-[10px];
}

:deep(.day-view) {
  @apply bg-white;
}

:deep(.vuecal__bg) {
  @apply bg-primary-30 ;
}

:deep(.vuecal__body) {
  @apply rounded-tl-[0px] rounded-tr-[0px] rounded-bl-[10px] rounded-br-[10px];
}

:deep(.vuecal__header) {
  @apply bg-primary-30 rounded-tl-[10px] rounded-tr-[10px];
}

:deep(.vuecal__title-bar) {
  @apply flex justify-center bg-primary-100 text-white h-[60px];
  @apply rounded-tl-[10px] rounded-tr-[10px];
}

:deep(.day-split-header) {
  flex: 1;
}
.break-words {
  word-break: break-word;
  white-space: normal!important;
}
:deep(.vuecal__flex) {
  .vuecal__title {
    @apply flex-none w-auto;
  }
  .vuecal__split-days-headers {
    @apply h-auto bg-primary-30 ml-[60px] border-gray-40 border;
  }
}

:deep(.vuecal__cell-split) {
  @apply border-r border-r-gray-50;
}
:deep(.vuecal__event) {
  @apply border-[4px] border-l-[8px] rounded-[4px] cursor-pointer text-left text-[12px];
}
:deep(.vuecal__event.no-shift) {
  @apply bg-gray-40 border-none opacity-60;
}
:deep(.vuecal__event.disabled) {
  background: repeating-linear-gradient(-45deg, transparent, transparent 5px, var(--gray-50) 5px, var(--gray-50) 11px);
  @apply text-gray-80 flex justify-center items-center ;
  @apply bg-gray-40 border-none opacity-60;
}

:deep(.vuecal__event.wait) {
  @apply bg-[#FBF2E7] border-[#E4A65F];
}
:deep(.vuecal__event.cancel) {
  @apply bg-danger-light border-danger;
}
:deep(.vuecal__event.confirm) {
  @apply bg-[#E1EDF9] border-[#3788D8];
}
:deep(.vuecal__event.complete) {
  @apply bg-[#F4F7E2] border-[#B5C93E];
}

:deep(.vuecal__time-column) {
  @apply w-[60px] bg-primary-30 ;
}

:deep(.cell-block) {
  @apply hover:bg-primary-30;
}
.tag {
  @apply border border-info text-info bg-info-light p-[2px] rounded-[4px] text-center;
  @apply w-[fit-content];
}
</style>
